/**
 * ElefaSipPlugin - Capacitor bridge to native iOS/Android SIP+libwebrtc engine.
 *
 * Native implementations:
 *   iOS    → native/ios/ElefaSip/ElefaSipPlugin.swift  (GoogleWebRTC.framework + SIP-over-WSS)
 *   Android→ native/android/elefa-sip/ElefaSipPlugin.kt (org.webrtc AAR + native SIP UAC)
 *
 * Web fallback returns a no-op so React code can safely call the plugin
 * regardless of platform (the dialer chooses the path via useIsNative()).
 */

import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';

export interface SipRegisterOptions {
  /** Legacy SIP/TLS URI returned by freeswitch-connect-start; iOS derives WSS from this only as fallback. */
  sipServerTls: string;
  /** Production SIP-over-WSS endpoint, e.g. wss://fs.elefa.tel:7443[/path]. */
  sipServerWss?: string;
  username: string;
  password: string;
  /** Optional outbound proxy override (defaults to sipServerTls host). */
  outboundProxy?: string;
  /** Display name shown in From header. */
  displayName?: string;
}

export interface SipIceServer {
  urls: string | string[];
  username?: string;
  credential?: string;
}

export interface SipCallOptions {
  /**
   * Optional client-supplied call identifier. The native plugin now owns
   * the authoritative callId (CallKit UUID = SIP Call-ID) and returns it
   * from `call()`. Kept for backwards compatibility / log correlation only.
   */
  callId?: string;
  destination: string;
  callerId: string;
  /** Display name shown in CallKit UI and From header (falls back to callerId). */
  displayName?: string;
  /** ASTPP accountcode for billing - sent as X-Accountcode SIP header. */
  accountcode?: string;
  /** Custom headers used by the CDR pipeline (X-User-Id, X-Call-Id, X-Caller-Id). */
  customHeaders?: Record<string, string>;
  /** Same ICE/TURN list used by the browser SIP.js path; required for native iOS NAT traversal. */
  iceServers?: SipIceServer[];
}

export type SipCallEvent =
  | { type: 'registered' }
  | { type: 'registrationFailed'; reason: string }
  | { type: 'progress'; callId: string }            // 180 / 183
  | { type: 'connected'; callId: string }           // 200 OK + media
  | { type: 'ended'; callId: string; reason: string; sipCode?: number }
  | { type: 'mediaStats'; callId: string; rttMs: number; lossPct: number; jitterMs: number }
  | { type: 'audioRouteChanged'; route: 'speaker' | 'receiver' | 'bluetooth' | 'wired' };

export interface VoipPushTokenEvent {
  /** Hex-encoded APNs VoIP device token. Empty string = invalidated. */
  token: string;
  platform: 'ios' | 'android';
  tokenType: 'apns_voip' | 'fcm';
}

export interface IncomingCallEvent {
  /** CallKit UUID (lowercased) - authoritative call id for the inbound leg. */
  callId: string;
  /** SIP Call-ID from the push payload (used to correlate the 200 OK). */
  sipCallId: string;
  callerId: string;
  callerName: string;
}

export interface AnswerInboundEvent {
  callId: string;
  sipCallId: string;
}

export interface ElefaSipPluginInterface {
  register(options: SipRegisterOptions): Promise<{ ok: boolean }>;
  unregister(): Promise<void>;
  /** Returns the authoritative callId (CallKit UUID = SIP Call-ID) generated by the native plugin. */
  call(options: SipCallOptions): Promise<{ ok: boolean; callId: string }>;
  hangup(options: { callId: string }): Promise<void>;
  setMuted(options: { muted: boolean }): Promise<void>;
  setSpeaker(options: { on: boolean }): Promise<void>;
  setHold(options: { hold: boolean }): Promise<void>;
  sendDTMF(options: { digits: string }): Promise<void>;
  /** Force ICE restart (called on Wi-Fi ↔ mobile handover). */
  restartIce(): Promise<void>;
  /** Native microphone permission request. */
  requestPermissions(): Promise<{ microphone?: string }>;
  /** P4: returns the cached APNs VoIP push token (empty until iOS issues it). */
  getVoipPushToken(): Promise<{ token: string; platform: string; tokenType: string }>;
  /** P4: programmatically accept the pending inbound call. */
  answer(): Promise<{ ok: boolean; callId: string }>;
  /** P4: decline the pending inbound call (CXEndCallAction → SIP 486/603). */
  declineIncoming(): Promise<{ ok: boolean }>;
  /**
   * Android-only audio prep (used by the WebView SIP.js path).
   * Sets AudioManager.MODE_IN_COMMUNICATION + earpiece routing so WebRTC
   * audio plays through STREAM_VOICE_CALL instead of the loudspeaker.
   * No-op on iOS (native ElefaSip owns the audio session there).
   */
  prepareAudio?(): Promise<void>;
  releaseAudio?(): Promise<void>;
  /** Android-only ringback (call-progress dial tone) via ToneGenerator. */
  startRingback?(): Promise<void>;
  stopRingback?(): Promise<void>;
  addListener(
    eventName: 'sipEvent',
    listener: (event: SipCallEvent) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'voipPushTokenUpdated',
    listener: (event: VoipPushTokenEvent) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'incomingCall',
    listener: (event: IncomingCallEvent) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'answerInbound',
    listener: (event: AnswerInboundEvent) => void,
  ): Promise<PluginListenerHandle>;
}

class ElefaSipWeb implements ElefaSipPluginInterface {
  async register() { console.log('[ElefaSip] web: register no-op'); return { ok: false }; }
  async unregister() {}
  async call() { console.log('[ElefaSip] web: call no-op'); return { ok: false, callId: '' }; }
  async hangup() {}
  async setMuted() {}
  async setSpeaker() {}
  async setHold() {}
  async sendDTMF() {}
  async restartIce() {}
  async requestPermissions() { return { microphone: 'unavailable' }; }
  async getVoipPushToken() { return { token: '', platform: 'web', tokenType: 'web_push' }; }
  async answer() { return { ok: false, callId: '' }; }
  async declineIncoming() { return { ok: false }; }
  async prepareAudio() {}
  async releaseAudio() {}
  async startRingback() {}
  async stopRingback() {}
  async addListener() {
    return { remove: async () => {} } as PluginListenerHandle;
  }
}
export const ElefaSipPlugin = registerPlugin<ElefaSipPluginInterface>('ElefaSip', {
  web: () => new ElefaSipWeb(),
});

/** True only when the native plugin is actually present on the device. */
export const isNativeSipAvailable = (): boolean =>
  Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('ElefaSip');
